/**
 * Kruimelpad *Instellingen › <pagina>* boven elke pagina die onder Instellingen valt (feedback 21, experiment).
 *
 * De shell zet hem neer en niet de pagina zelf: de losse pagina's (Categorieën, Workflows, …) houden hun eigen
 * adres en weten niet dat ze een tegel zijn. Eén plek, dus één opmaak.
 *
 * Op de pagina zelf is het laatste stuk geen link maar `aria-current="page"`; op een dieper adres
 * (`/workflows/12`) is het wél een link terug naar de lijst.
 *
 * LOKALE afwijking: het package heeft een `Breadcrumb`, maar die zet een vaste Engelse `aria-label` en rendert
 * gewone `<a href>`-links — een volledige paginalading, langs de router en de navigatie-guard voor niet-opgeslagen
 * wijzigingen heen. Deze volgt dezelfde opbouw (`nav > ol > li`, caret als scheiding, `aria-current` op de
 * huidige pagina) met router-links en een vertaalde naam. Zie AURORA-NOTES.md.
 */
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { CaretRight } from '@phosphor-icons/react';
import type { MouseEvent } from 'react';

import { confirmNavigation } from '@shared/navigationGuard';

import type { SettingsPage } from './settingsPages';
import styles from './SettingsBreadcrumb.module.css';

export function SettingsBreadcrumb({ page }: { page: SettingsPage }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const onPage = pathname === page.path;
  // Net als het menu: een pagina met niet-opgeslagen wijzigingen mag het weggaan tegenhouden.
  const guard = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!confirmNavigation()) event.preventDefault();
  };

  return (
    <nav className={styles.breadcrumb} aria-label={t('settingsHub.breadcrumb')}>
      <ol className={styles.list}>
        <li className={styles.item}>
          <Link to="/settings" className={styles.link} onClick={guard}>{t('nav.settings')}</Link>
        </li>
        <li className={styles.item}>
          <CaretRight size={14} weight="regular" className={styles.separator} aria-hidden="true" />
          {onPage
            ? <span aria-current="page">{t(page.labelKey)}</span>
            : <Link to={page.path} className={styles.link} onClick={guard}>{t(page.labelKey)}</Link>}
        </li>
      </ol>
    </nav>
  );
}
